import { MessageCircle, AlertCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";

const Services = () => {
  const { t, lang } = useLanguage();
  const sp = t.servicesPage;

  return (
    <div data-testid="services-page">
      <Seo
        title={lang === "es" ? "Servicios — NIE, TIE, traducción y más | Marina Knows" : "Services — NIE, TIE, Translation & More | Marina Knows"}
        description={lang === "es"
          ? "Todos los servicios de Marina Knows: NIE, TIE, empadronamiento, alta de autónomo, traducción e interpretación, vivienda, banca, salud y familia en la Comunidad Valenciana."
          : "All Marina Knows services: NIE, TIE, empadronamiento, autónomo registration, professional translation & interpretation, housing, banking, healthcare and family help in the Comunidad Valenciana."}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-14">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-olive mb-4">{sp.overline}</p>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tighter text-ink mb-6 max-w-3xl" data-testid="services-title">{sp.title}</h1>
        <p className="text-lg text-ink/70 max-w-2xl" data-testid="services-sub">{sp.sub}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 space-y-16">
        {sp.groups.map((group, gi) => (
          <div key={gi} data-testid={`service-group-${gi}`}>
            <Reveal>
              <h2 className="font-serif text-3xl md:text-4xl text-ink mb-8 flex items-baseline gap-4">
                <span className="font-mono text-xs text-olive">[ 0{gi + 1} ]</span>
                {group.name}
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {group.items.map((item, ii) => (
                <Reveal key={ii} delay={ii * 0.04}>
                  <div className="bg-white border border-stone rounded-2xl p-7 h-full flex flex-col transition-all hover:border-terracotta/50 hover:-translate-y-1" data-testid={`service-card-${gi}-${ii}`}>
                    <div className="flex justify-between items-start gap-4 mb-3">
                      <h3 className="font-medium text-lg text-ink leading-snug">{item.name}</h3>
                      <span className="font-mono text-bronze font-medium whitespace-nowrap">{item.price}</span>
                    </div>
                    <p className="text-ink/65 leading-relaxed mb-4">{item.desc}</p>
                    {item.unit && (
                      <p className="mt-auto font-mono text-xs text-stone-fg">{sp[item.unit]}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
            {gi === 1 && (
              <Reveal delay={0.1}>
                <div className="mt-8 bg-olive-soft/40 border border-olive/30 rounded-2xl p-7 flex gap-4" data-testid="sworn-note">
                  <AlertCircle size={22} className="text-olive-dark shrink-0 mt-0.5" aria-hidden />
                  <div>
                    <p className="font-medium text-ink mb-1.5">{sp.swornTitle}</p>
                    <p className="text-ink/70 leading-relaxed">{sp.swornBody}</p>
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        ))}

        <Reveal>
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-cta"
            className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-ink font-medium px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
          >
            <MessageCircle size={18} aria-hidden />
            {sp.cta}
          </a>
        </Reveal>
      </section>
    </div>
  );
};

export default Services;
